import { useInfiniteQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type { GetUserConversationParams, UserConversationResponse, MappedUserConversationResponse } from './types';

function conversationMessagesMapper(response: UserConversationResponse): MappedUserConversationResponse {
  const conversationData = response.data;
  const messages = conversationData.messages || [];

  // Sort messages in chronological order (oldest first) for proper conversation flow
  const sortedMessages = [...messages].sort((a, b) => {
    const dateA = typeof a.created_at === 'number' ? a.created_at : new Date(a.created_at || 0).getTime();
    const dateB = typeof b.created_at === 'number' ? b.created_at : new Date(b.created_at || 0).getTime();
    return dateA - dateB; // Ascending order (oldest first)
  });

  return {
    conversation: {
      id: conversationData.id,
      title: conversationData.title,
      artist_id: conversationData.artist_id,
      user_id: conversationData.user_id,
      user_name: conversationData.user_name,
      user_profile_image: conversationData.user_profile_image,
      status: conversationData.status,
    },
    messages: sortedMessages,
    lastItemIdentifier: response.last_item_identifier,
  };
}

export async function getUserConversation({
  artistId,
  conversationId,
  limit = 250,
  startAt,
}: GetUserConversationParams): Promise<MappedUserConversationResponse> {
  if (!artistId) throw Error('Artist ID is required');
  if (!conversationId) throw Error('Conversation ID is required');

  const params: Record<string, string | number> = { limit };

  if (startAt) {
    params.cursor = startAt;
  }

  const url = `/web/studio/artists/${artistId}/conversations/${conversationId}`;
  const response = await api.executeGetRequest<UserConversationResponse>(url, params);

  return conversationMessagesMapper(response.data);
}

export function useUserConversation(artistId: string, conversationId: string | null, limit = 250) {
  const user = storage.getUser();

  const query = useInfiniteQuery({
    initialPageParam: undefined,
    enabled: !!artistId && !!conversationId && !!user?.is_registered,
    queryKey: queryKeys.artistChatbotConversation(artistId, conversationId || ''),
    queryFn: ({ pageParam }) =>
      getUserConversation({
        artistId,
        conversationId: conversationId!,
        limit,
        startAt: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      // Only return next page cursor if we have messages and a valid cursor
      if (!lastPage.messages || lastPage.messages.length === 0) {
        return undefined;
      }
      // If lastItemIdentifier is null or empty, no more pages
      if (!lastPage.lastItemIdentifier) {
        return undefined;
      }
      return lastPage.lastItemIdentifier;
    },
  });

  // Flatten all pages into a single array of messages
  const messages = query.data?.pages?.flatMap((page) => page.messages) || [];
  const conversation = query.data?.pages?.[0]?.conversation || null;

  return {
    ...query,
    messages,
    conversation,
  };
}
